/**
 * <s-pattern-viewer> — Opens a color chart pattern card in a full-screen photo viewer.
 *
 * Child markup:
 *   article[data-pattern]          — One pattern card. Its figure > img elements and
 *                                     figcaption text become the viewer's photos.
 *   [data-pattern-open]            — Button inside a card that opens the viewer.
 *   dialog[data-viewer-dialog]     — The shared viewer dialog, containing:
 *     [data-viewer-title]          — Receives the pattern name.
 *     [data-viewer-description]    — Receives the card's [data-pattern-description] text;
 *                                     hidden when the pattern has no description.
 *     img[data-viewer-image]       — The large photo.
 *     [data-viewer-label]          — Receives the current view label (Night, Day…).
 *     [data-viewer-count]          — Receives "current / total".
 *     [data-viewer-thumbs]         — Receives one thumbnail button per photo.
 *     [data-viewer-prev], [data-viewer-next] — Step through photos.
 *     [data-viewer-prev-pattern], [data-viewer-next-pattern] — Jump to the previous /
 *                                     next pattern card; [data-viewer-prev-name] and
 *                                     [data-viewer-next-name] receive those cards' names.
 * Keyboard: ArrowLeft / ArrowRight step through photos while the viewer is open.
 */
class SPatternViewer extends HTMLElement {
  #dialog = null;
  #photos = [];
  #index = 0;
  #cards = [];
  #cardIndex = 0;
  #abort = null;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-viewer-dialog]');
    if (!this.#dialog) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-pattern-open]');
      if (opener && this.contains(opener)) {
        this.#open(opener.closest('[data-pattern]'));
        return;
      }
      if (event.target.closest('[data-viewer-prev]')) this.#show(this.#index - 1);
      if (event.target.closest('[data-viewer-next]')) this.#show(this.#index + 1);
      if (event.target.closest('[data-viewer-prev-pattern]')) this.#stepCard(-1);
      if (event.target.closest('[data-viewer-next-pattern]')) this.#stepCard(1);
      const thumb = event.target.closest('[data-viewer-thumb]');
      if (thumb) this.#show(Number(thumb.dataset.viewerThumb));
    }, { signal });

    this.#dialog.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(this.#dialog).direction === 'rtl';
      if (event.key === 'ArrowRight') this.#show(this.#index + (rtl ? -1 : 1));
      if (event.key === 'ArrowLeft') this.#show(this.#index + (rtl ? 1 : -1));
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #cardTitle(card) {
    return card?.querySelector('h3')?.textContent.trim() ?? '';
  }

  #stepCard(step) {
    const total = this.#cards.length;
    if (total < 2) return;
    this.#open(this.#cards[(this.#cardIndex + step + total) % total]);
  }

  #updateCardNav() {
    const total = this.#cards.length;
    const prev = this.#cards[(this.#cardIndex - 1 + total) % total];
    const next = this.#cards[(this.#cardIndex + 1) % total];
    const prevName = this.#dialog.querySelector('[data-viewer-prev-name]');
    const nextName = this.#dialog.querySelector('[data-viewer-next-name]');
    if (prevName) prevName.textContent = this.#cardTitle(prev);
    if (nextName) nextName.textContent = this.#cardTitle(next);
  }

  #open(card) {
    if (!card) return;
    this.#cards = Array.from(this.querySelectorAll('[data-pattern]')).filter((item) => item.querySelector('figure img') && !item.closest('[hidden]'));
    this.#cardIndex = Math.max(0, this.#cards.indexOf(card));
    const title = card.querySelector('h3')?.textContent.trim() ?? '';
    this.#photos = Array.from(card.querySelectorAll('figure')).map((figure) => {
      const img = figure.querySelector('img');
      return {
        src: img?.currentSrc || img?.src || '',
        srcset: img?.getAttribute('srcset') || '',
        alt: img?.alt || '',
        label: figure.querySelector('figcaption')?.textContent.trim() ?? '',
      };
    }).filter((photo) => photo.src);
    if (!this.#photos.length) return;

    this.#dialog.querySelector('[data-viewer-title]').textContent = title;
    const description = card.querySelector('[data-pattern-description]')?.textContent.trim() ?? '';
    const descriptionSlot = this.#dialog.querySelector('[data-viewer-description]');
    if (descriptionSlot) {
      descriptionSlot.textContent = description;
      descriptionSlot.hidden = !description;
    }
    const thumbs = this.#dialog.querySelector('[data-viewer-thumbs]');
    thumbs.replaceChildren(...this.#photos.map((photo, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.viewerThumb = String(i);
      button.className = 'viewer-thumb';
      button.setAttribute('aria-label', photo.label);
      const img = document.createElement('img');
      img.src = photo.src;
      img.alt = '';
      const label = document.createElement('span');
      label.textContent = photo.label;
      button.append(img, label);
      return button;
    }));

    this.#updateCardNav();
    this.#show(0);
    if (!this.#dialog.open) this.#dialog.showModal();
  }

  #show(target) {
    const total = this.#photos.length;
    if (!total) return;
    this.#index = (target + total) % total;
    const photo = this.#photos[this.#index];
    const image = this.#dialog.querySelector('[data-viewer-image]');
    if (photo.srcset) image.setAttribute('srcset', photo.srcset);
    else image.removeAttribute('srcset');
    image.src = photo.src;
    image.alt = photo.alt;
    this.#dialog.querySelector('[data-viewer-label]').textContent = photo.label;
    this.#dialog.querySelector('[data-viewer-count]').textContent = `${this.#index + 1} / ${total}`;
    this.#dialog.querySelectorAll('[data-viewer-thumb]').forEach((thumb, i) => {
      if (i === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-pattern-viewer')) {
  customElements.define('s-pattern-viewer', SPatternViewer);
}
